import { listDeploymentsForVendorServiceModel } from "./registry";
import type {
  CompliancePolicy,
  PolicyDecision,
  PolicyViolationReason,
  Service,
  TrainingPolicy,
  Vendor,
  Deployment,
} from "./types";

export const DEFAULT_COMPLIANCE_POLICY: CompliancePolicy = {
  trainingPolicy: "not_allowed",
  requireZdr: false,
  requireBaa: false,
  bannedProviders: [],
};

// A deployment passes when its terms are at most as permissive as the policy
// tolerates.
const TRAINING_POLICY_RANK = {
  not_allowed: 0,
  customer_scoped: 1,
  allowed: 2,
} as const satisfies Record<TrainingPolicy, number>;

export const isDeploymentAllowed = (
  deployment: Deployment,
  policy: CompliancePolicy
): PolicyDecision => {
  if (policy.bannedProviders.includes(deployment.vendor)) {
    return { isAllowed: false, reason: "vendor_banned" };
  }

  if (
    TRAINING_POLICY_RANK[deployment.trainingPolicy] >
    TRAINING_POLICY_RANK[policy.trainingPolicy]
  ) {
    return { isAllowed: false, reason: "training_not_allowed" };
  }

  if (policy.requireZdr && !deployment.hasZdr) {
    return { isAllowed: false, reason: "zdr_required" };
  }

  if (policy.requireBaa && !deployment.hasBaa) {
    return { isAllowed: false, reason: "baa_required" };
  }

  return { isAllowed: true };
};

export const listAllowedDeploymentsForVendorServiceModel = ({
  vendor,
  service,
  model,
  policy,
}: {
  vendor: Vendor;
  service: Service;
  model?: string;
  policy: CompliancePolicy;
}): Deployment[] =>
  listDeploymentsForVendorServiceModel({ vendor, service, model }).filter(
    (deployment) => isDeploymentAllowed(deployment, policy).isAllowed
  );

// Allowed when any matching deployment passes. `reason` is absent when no
// registry row matches, which fails closed.
export const getVendorServiceModelDecision = ({
  vendor,
  service,
  model,
  policy,
}: {
  vendor: Vendor;
  service: Service;
  model?: string;
  policy: CompliancePolicy;
}):
  | { isAllowed: true }
  | { isAllowed: false; reason?: PolicyViolationReason } => {
  const decisions = listDeploymentsForVendorServiceModel({
    vendor,
    service,
    model,
  }).map((deployment) => isDeploymentAllowed(deployment, policy));
  if (decisions.some((decision) => decision.isAllowed)) {
    return { isAllowed: true };
  }
  return {
    isAllowed: false,
    reason: decisions.flatMap((decision) =>
      decision.isAllowed ? [] : [decision.reason]
    )[0],
  };
};
